// src/components/login-form.tsx
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useRouter } from "@tanstack/react-router";

import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth-client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const loginSchema = z.object({
	email: z.string().email(),
	password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps extends React.ComponentProps<"div"> {
	redirectUrl?: string;
}

export function SignInForm({
	className,
	redirectUrl,
	...props
}: LoginFormProps) {
	const router = useRouter();
	const [serverError, setServerError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isGuestLoading, setIsGuestLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const form = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = form;

	const onSubmit = async (data: LoginFormData) => {
		setIsLoading(true);
		setServerError("");

		try {
			const res = await authClient.signIn.email(data);

			// 👇 Check response manually
			if (res.error || !res.data?.user) {
				const msg = res.error?.message || "Invalid email or password.";
				setServerError(msg);
				toast.error(msg);
				return;
			}

			toast.success("Signed in successfully!");
			if (redirectUrl) {
				await router.navigate({ to: redirectUrl });
			} else {
				await router.navigate({
					to: "/$workspaceId",
					params: { workspaceId: res.data.user.lastWorkspaceId },
				});
			}
		} catch (err) {
			const msg = err instanceof Error ? err.message : "Something went wrong.";
			setServerError(msg);
			toast.error(msg);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGuestSignIn = async () => {
		setIsGuestLoading(true);
		setServerError("");

		try {
			const res = await authClient.signIn.anonymous();

			if (res.error || !res.data?.user || !res.data.user.lastWorkspaceId) {
				const msg = res.error?.message || "Failed to continue as guest.";
				setServerError(msg);
				toast.error(msg);
				return;
			}

			const lastWorkspaceId = res.data.user.lastWorkspaceId;

			toast.success("Continuing as guest!");
			if (redirectUrl) {
				await router.navigate({ to: redirectUrl });
			} else {
				await router.navigate({
					to: "/$workspaceId",
					params: { workspaceId: lastWorkspaceId },
				});
			}
		} catch (err) {
			const msg =
				err instanceof Error ? err.message : "Failed to continue as guest.";
			setServerError(msg);
			toast.error(msg);
		} finally {
			setIsGuestLoading(false);
		}
	};

	return (
		<div className={cn("flex flex-col gap-6", className)} {...props}>
			<Card className="overflow-hidden p-0">
				<CardContent className="grid grid-cols-1 p-0 md:grid-cols-2">
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="flex flex-col gap-6 p-6 md:p-8"
					>
						<div className="text-center">
							<h1 className="font-bold text-2xl">Welcome back</h1>
							<p className="text-muted-foreground text-sm">
								Sign in to your account
							</p>
						</div>

						<div className="grid gap-3">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								placeholder="m@example.com"
								{...register("email")}
							/>
							{errors.email && (
								<p className="text-red-500 text-sm">{errors.email.message}</p>
							)}
						</div>

						<div className="grid gap-3">
							<div className="flex items-center">
								<Label htmlFor="password">Password</Label>
								<a
									href="#"
									className="ml-auto text-sm underline-offset-2 hover:underline"
								>
									Forgot your password?
								</a>
							</div>
							<div className="relative">
								<Input
									id="password"
									type={showPassword ? "text" : "password"}
									placeholder="••••••••"
									{...register("password")}
									className="pr-10"
								/>
								<button
									type="button"
									onClick={() => setShowPassword((prev) => !prev)}
									className="-translate-y-1/2 absolute top-1/2 right-3 text-muted-foreground hover:text-foreground"
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
								</button>
							</div>
							{errors.password && (
								<p className="text-red-500 text-sm">
									{errors.password.message}
								</p>
							)}
						</div>

						{serverError && (
							<p className="text-center text-red-500 text-sm">{serverError}</p>
						)}

						<Button
							type="submit"
							className="w-full"
							disabled={isLoading || isGuestLoading}
						>
							{isLoading ? "Signing in..." : "Sign in"}
						</Button>

						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<span className="w-full border-t" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-white px-2 text-gray-500">Or</span>
							</div>
						</div>

						<Button
							type="button"
							variant="outline"
							className="w-full"
							disabled={isLoading || isGuestLoading}
							onClick={() => {
								const searchParams = redirectUrl
									? `?redirect=${encodeURIComponent(redirectUrl)}`
									: "";
								router.navigate({ to: `/sign-up${searchParams}` });
							}}
						>
							Create Account
						</Button>

						<Button
							type="button"
							variant="ghost"
							className="w-full"
							disabled={isLoading || isGuestLoading}
							onClick={handleGuestSignIn}
						>
							{isGuestLoading ? "Continuing..." : "Continue as Guest"}
						</Button>
					</form>

					<div className="relative hidden bg-muted md:block">
						<img
							src="https://images.pexels.com/photos/2041627/pexels-photo-2041627.jpeg"
							alt="Login image"
							className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
						/>
					</div>
				</CardContent>
			</Card>

			<div className="text-balance text-center text-muted-foreground text-xs *:[a]:underline *:[a]:underline-offset-4 *:[a]:hover:text-primary">
				By continuing, you agree to our <a href="#">Terms of Service</a> and{" "}
				<a href="#">Privacy Policy</a>.
			</div>
		</div>
	);
}
